import { type CreateHelpdeskRequestInput, CreateHelpdeskRequestResponse } from "@repo/shared-types/helpdesk";
import { RequestError } from "../../shared/web/client.ts";
import { recordRequestFailure } from "../../shared/web/request-failures.ts";

export type HelpdeskSubmissionAttempt = { input: CreateHelpdeskRequestInput; files: { fieldId: string; file: File }[] };

const FALLBACK_MESSAGE = "There was a problem with your submission. Please retry or report if it keeps failing.";
const PREACCEPTANCE_FAILURES = new Set(["attachment_upload_failed", "catalog_unavailable", "directory_unavailable", "linear_unavailable"]);
const REJECTED_STATUSES = new Set([400, 401, 403, 404, 409, 413, 415, 422, 429]);

function submissionCode(error: unknown): string | undefined {
  return error instanceof RequestError && typeof error.body === "object" && error.body !== null && "error" in error.body && typeof error.body.error === "string" ? error.body.error : undefined;
}

export function isDirectoryOutage(error: unknown): boolean {
  return error instanceof RequestError && error.status === 503 && submissionCode(error) === "directory_unavailable";
}

export function submissionMayBeAccepted(error: unknown): boolean {
  if (!(error instanceof RequestError)) return true;
  if (REJECTED_STATUSES.has(error.status)) return false;
  return !(error.status === 503 && PREACCEPTANCE_FAILURES.has(submissionCode(error) ?? ""));
}

export function submissionErrorMessage(error: unknown): string {
  if (error instanceof RequestError) {
    if (error.status === 413) return "Attachments must be under 10 MiB each and the complete submission under 50 MiB.";
    if (error.status === 409) return "This form changed and can no longer accept this submission. Your answers are still here.";
    if (error.status === 422 && submissionCode(error) === "invalid_linear_share_recipient")
      return "One or more selected Linear recipients changed or are no longer eligible. Review the Linear access list and submit again; your selections are still here.";
    if (error.status === 422) return "Review the highlighted answers and submit again.";
    if (isDirectoryOutage(error)) return "The people directory is temporarily unavailable. Your answers and selected people are still here; try again.";
    if (error.status === 503) {
      if (submissionCode(error) === "attachment_upload_failed") return "The file upload failed. Retry, or remove optional attachments and send them to the coordinator later.";
      if (submissionCode(error) === "catalog_unavailable") return "The resource directory is temporarily unavailable. Your selections are still here; try again.";
      if (submissionCode(error) === "linear_unavailable") return "The Linear connection is temporarily unavailable. Your answers are still here; try again.";
    }
  }
  return FALLBACK_MESSAGE;
}

export function confirmedSubmission(response: unknown, path: string): CreateHelpdeskRequestResponse {
  const parsed = CreateHelpdeskRequestResponse.safeParse(response);
  if (parsed.success) return parsed.data;
  recordRequestFailure({ path, route: path, method: "POST", status: null, errorCode: "invalid_response", durationMs: 0 });
  throw new TypeError("Submission response was not recognized");
}
